import { MOD_ENTER, TaskActionBar, type TaskAction } from '@/features/contribution-kit';
import type { translations } from '@/translations/translations';
import type { TranscriptionAudioCopy } from './transcription-audio-copy';

type TranscriptionAudioActionBarProps = {
  t: Pick<(typeof translations)['fr'], 'previous'>;
  copy: TranscriptionAudioCopy;
  canGoPrevious: boolean;
  /** False hides "previous" where the queue has no history (initial-data transcription). */
  canGoBack?: boolean;
  hasAudio: boolean;
  canSend: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  onPrevious: () => void;
  onSkip: () => void;
  onSend: () => void;
  onTogglePlay: () => void;
};

/**
 * Bottom bar of "Transcrire": previous, skip, send. The handlers keep their
 * own checks and toasts. Keys (outside the text fields): Space plays, Enter
 * sends, left arrow goes back.
 */
export function TranscriptionAudioActionBar({
  t,
  copy,
  canGoPrevious,
  canGoBack = true,
  hasAudio,
  canSend,
  isLoading,
  isSubmitting,
  onPrevious,
  onSkip,
  onSend,
  onTogglePlay,
}: TranscriptionAudioActionBarProps) {
  const previous: TaskAction[] = canGoBack
    ? [{ key: 'previous', label: t.previous, icon: 'arrowLeft', onClick: onPrevious, disabled: !canGoPrevious, shortcut: 'ArrowLeft' }]
    : [];
  return (
    <TaskActionBar
      actions={[
        ...previous,
        { key: 'skip', label: copy.skip, icon: 'chevronRight', onClick: onSkip, disabled: isLoading || isSubmitting, testId: 'transcription-audio-skip' },
        {
          key: 'send',
          label: copy.send,
          icon: 'send',
          tone: 'primary',
          onClick: onSend,
          disabled: isLoading || !canSend,
          loading: isSubmitting,
          // Sends from the text field (Ctrl/Cmd+Enter), plain Enter elsewhere.
          shortcut: MOD_ENTER,
          testId: 'transcription-audio-send',
        },
      ]}
      extraShortcuts={[{ key: ' ', run: onTogglePlay, enabled: hasAudio }]}
      hints={hasAudio ? [{ key: ' ', label: copy.playHint }] : []}
    />
  );
}
